import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/card";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Switch } from "@repo/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  deleteProviderSettings,
  fetchProviderSettings,
  type ProviderSettingsRecord,
  upsertProviderSettings,
} from "@/lib/api";

const AVAILABLE_PROVIDERS = ["firecrawl_scrape", "firecrawl_agent", "zyte", "direct"];

const DEFAULT_FORM: ProviderForm = {
  use_auto: true,
  enabled_providers: ["firecrawl_scrape", "zyte", "direct"],
  provider_preference: ["firecrawl_scrape", "zyte", "direct"],
  retry_count: 2,
  timeout_ms: 45000,
};

interface ProviderForm {
  use_auto: boolean;
  enabled_providers: string[];
  provider_preference: string[];
  retry_count: number;
  timeout_ms: number;
}

interface AdapterProviderSettingsCardProps {
  adapterSlug: string;
}

/**
 * Per-adapter provider override editor.
 *
 * Loads + saves to `scrape_provider_settings` scoped to this adapter for
 * both `discovery` and `detail` operations. When an override is missing,
 * shows a placeholder indicating that global settings apply.
 */
export function AdapterProviderSettingsCard({ adapterSlug }: AdapterProviderSettingsCardProps) {
  const { toast } = useToast();
  const [activeOp, setActiveOp] = useState<"detail" | "discovery">("detail");
  const [hasOverride, setHasOverride] = useState<Record<"detail" | "discovery", boolean>>({
    detail: false,
    discovery: false,
  });
  const [forms, setForms] = useState<Record<"detail" | "discovery", ProviderForm>>({
    detail: { ...DEFAULT_FORM },
    discovery: { ...DEFAULT_FORM },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const applyRecord = useCallback(
    (op: "detail" | "discovery", record: ProviderSettingsRecord | null) => {
      setHasOverride((prev) => ({ ...prev, [op]: Boolean(record) }));
      if (record) {
        setForms((prev) => ({
          ...prev,
          [op]: {
            use_auto: record.use_auto,
            enabled_providers: record.enabled_providers,
            provider_preference: record.provider_preference,
            retry_count: record.retry_count,
            timeout_ms: record.timeout_ms,
          },
        }));
      } else {
        setForms((prev) => ({ ...prev, [op]: { ...DEFAULT_FORM } }));
      }
    },
    [],
  );

  const reload = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [detail, discovery] = await Promise.all([
        fetchProviderSettings("detail", adapterSlug, true),
        fetchProviderSettings("discovery", adapterSlug, true),
      ]);
      applyRecord("detail", detail);
      applyRecord("discovery", discovery);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Provider settings unavailable");
    } finally {
      setIsLoading(false);
    }
  }, [adapterSlug, applyRecord]);

  useEffect(() => {
    reload();
  }, [reload]);

  const current = forms[activeOp];

  const updateCurrent = (patch: Partial<ProviderForm>) => {
    setForms((prev) => ({ ...prev, [activeOp]: { ...prev[activeOp], ...patch } }));
  };

  const toggleEnabled = (provider: string) => {
    const next = current.enabled_providers.includes(provider)
      ? current.enabled_providers.filter((p) => p !== provider)
      : [...current.enabled_providers, provider];
    const nextPreference = current.provider_preference.includes(provider)
      ? current.provider_preference
      : [...current.provider_preference, provider];
    updateCurrent({
      enabled_providers: next.length > 0 ? next : ["firecrawl_scrape"],
      provider_preference: nextPreference,
    });
  };

  const togglePreference = (provider: string) => {
    const next = current.provider_preference.includes(provider)
      ? current.provider_preference.filter((p) => p !== provider)
      : [...current.provider_preference, provider];
    updateCurrent({
      provider_preference: next.length > 0 ? next : ["firecrawl_scrape"],
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    const result = await upsertProviderSettings({
      scope: "adapter",
      adapter_slug: adapterSlug,
      operation_type: activeOp,
      use_auto: current.use_auto,
      enabled_providers: current.enabled_providers,
      provider_preference: current.provider_preference,
      retry_count: current.retry_count,
      timeout_ms: current.timeout_ms,
      metadata: {},
    });
    setIsSaving(false);
    if (!result) {
      toast({
        title: "Save failed",
        description: "Could not save adapter provider override.",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Provider override saved",
      description: `${activeOp === "detail" ? "Detail" : "Discovery"} override updated.`,
    });
    applyRecord(activeOp, result);
  };

  const handleClearOverride = async () => {
    setIsClearing(true);
    const ok = await deleteProviderSettings("adapter", activeOp, adapterSlug);
    setIsClearing(false);
    if (!ok) {
      toast({
        title: "Clear failed",
        description: "Could not delete adapter override.",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Override cleared",
      description: `Adapter will now use global ${activeOp} provider settings.`,
    });
    applyRecord(activeOp, null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Provider override</CardTitle>
        <CardDescription>
          These settings control the Supabase edge queue path. Firebase workers and the live
          preview use their own host metrics and Bright Data routing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadError && <p className="text-sm text-destructive">Could not load provider settings: {loadError}</p>}
        <Tabs
          value={activeOp}
          onValueChange={(value) => setActiveOp(value as "detail" | "discovery")}
        >
          <TabsList>
            <TabsTrigger value="detail">
              Detail
              {hasOverride.detail ? (
                <Badge variant="default" className="ml-2 text-[10px]">override</Badge>
              ) : (
                <Badge variant="outline" className="ml-2 text-[10px]">global</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="discovery">
              Discovery
              {hasOverride.discovery ? (
                <Badge variant="default" className="ml-2 text-[10px]">override</Badge>
              ) : (
                <Badge variant="outline" className="ml-2 text-[10px]">global</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {(["detail", "discovery"] as const).map((op) => (
            <TabsContent key={op} value={op} className="space-y-4 pt-2">
              {isLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : loadError ? null : (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor={`auto-${op}`}>Use auto selection</Label>
                      <p className="text-xs text-muted-foreground">
                        Pick the best-performing provider from history before falling back.
                      </p>
                    </div>
                    <Switch
                      id={`auto-${op}`}
                      checked={current.use_auto}
                      onCheckedChange={(checked) => updateCurrent({ use_auto: checked })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Enabled providers</Label>
                    <div className="flex flex-wrap gap-2">
                      {AVAILABLE_PROVIDERS.map((provider) => (
                        <Badge
                          key={provider}
                          variant={
                            current.enabled_providers.includes(provider) ? "default" : "outline"
                          }
                          className="cursor-pointer"
                          onClick={() => toggleEnabled(provider)}
                        >
                          {provider}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Preference order</Label>
                    <div className="flex flex-wrap gap-2">
                      {AVAILABLE_PROVIDERS.map((provider) => (
                        <Badge
                          key={provider}
                          variant={
                            current.provider_preference.includes(provider)
                              ? "secondary"
                              : "outline"
                          }
                          className="cursor-pointer"
                          onClick={() => togglePreference(provider)}
                        >
                          {provider}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor={`retry-${op}`}>Retry count</Label>
                      <Input
                        id={`retry-${op}`}
                        name={`retry-${op}`}
                        type="number"
                        min={0}
                        max={8}
                        value={current.retry_count}
                        onChange={(event) =>
                          updateCurrent({
                            retry_count: parseInt(event.target.value || "0", 10),
                          })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`timeout-${op}`}>Timeout (ms)</Label>
                      <Input
                        id={`timeout-${op}`}
                        name={`timeout-${op}`}
                        type="number"
                        min={1000}
                        step={500}
                        value={current.timeout_ms}
                        onChange={(event) =>
                          updateCurrent({
                            timeout_ms: parseInt(event.target.value || "1000", 10),
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 justify-end pt-1">
                    {hasOverride[op] && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleClearOverride}
                        disabled={isClearing}
                      >
                        {isClearing ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RotateCcw className="w-4 h-4 mr-2" />
                        )}
                        Clear override
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSave}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save override
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
